/**
 * MoonyModal — the chatbot overlay.
 *
 * v2 bugs fixed here
 * ------------------
 * 1. The modal was rendered *inside* the section that owns a `transform`
 *    (framer-motion animates transforms on every animated element) and inside a
 *    `backdrop-filter` panel. Both create a containing block, so the modal's
 *    `position: fixed` resolved against that ancestor instead of the viewport —
 *    it drifted, clipped, and scrolled away with the page.
 *    FIX: createPortal(…, document.body). No transformed or filtered ancestor
 *    exists above it, so fixed positioning is honest.
 * 2. The CRT overlays sit at z-index 900-920 and cover the whole viewport with
 *    pointer-events:none. The v2 modal had no defined layer, so it painted
 *    *under* the scanlines and grain.
 *    FIX: --z-modal (1100) is above the whole CRT stack (see tokens.css).
 * 3. Theatrical animations were declared on the wrapper and interrupted by the
 *    page's snap scroller, so entrance/exit never completed.
 *    FIX: AnimatePresence owns mount/unmount, and the document scroll is
 *    locked for the modal's lifetime (useScrollLock).
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Send, X } from 'lucide-react';
import { askMoony } from '@/lib/api';
import { useScrollLock } from '@/lib/useScrollLock';

const MAX_TURNS = 40;

const WELCOME = {
  id: 'welcome',
  role: 'moony',
  text: "...you found me. i'm moony. i keep the night shift here.\nask me something, or don't. i'll be up either way.",
};

/** Entrance: rises from the bottom sheet position on mobile, scales in above it. */
const MODAL_VARIANTS = {
  hidden: { opacity: 0, y: 24, scale: 0.97 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.34, ease: [0.16, 1, 0.3, 1] },
  },
  exit: {
    opacity: 0,
    y: 16,
    scale: 0.97,
    transition: { duration: 0.2, ease: [0.65, 0, 0.35, 1] },
  },
};

const LINE_VARIANTS = {
  hidden: { opacity: 0, y: 8 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.26, ease: [0.16, 1, 0.3, 1] } },
};

function Bubble({ line }) {
  if (line.role === 'system') {
    return (
      <motion.div
        variants={LINE_VARIANTS}
        initial="hidden"
        animate="visible"
        className="dmr-bubble dmr-bubble--system"
      >
        {line.text}
      </motion.div>
    );
  }

  return (
    <motion.div
      variants={LINE_VARIANTS}
      initial="hidden"
      animate="visible"
      className={`dmr-bubble dmr-bubble--${line.role === 'user' ? 'user' : 'moony'}`}
    >
      {line.text}
    </motion.div>
  );
}

export default function MoonyModal({ open, onClose }) {
  const [lines, setLines] = useState([WELCOME]);
  const [draft, setDraft] = useState('');
  const [isThinking, setIsThinking] = useState(false);
  const [error, setError] = useState(null);

  const inputRef = useRef(null);
  const bodyRef = useRef(null);
  const requestId = useRef(0);

  useScrollLock(open);

  // Keep the transcript pinned to the newest line.
  useEffect(() => {
    const body = bodyRef.current;
    if (body) body.scrollTop = body.scrollHeight;
  }, [lines, isThinking]);

  // Focus the composer once the entrance animation has settled.
  useEffect(() => {
    if (!open) return undefined;
    const timer = setTimeout(() => inputRef.current?.focus(), 320);
    return () => clearTimeout(timer);
  }, [open]);

  const escape = useCallback(
    (event) => {
      if (event.key === 'Escape') onClose();
    },
    [onClose],
  );

  useEffect(() => {
    if (!open) return undefined;
    window.addEventListener('keydown', escape);
    return () => window.removeEventListener('keydown', escape);
  }, [open, escape]);

  const history = useMemo(
    () =>
      lines
        .filter((line) => line.role !== 'system' && line.id !== 'welcome')
        .map((line) => ({
          role: line.role === 'user' ? 'user' : 'moony',
          content: line.text,
        })),
    [lines],
  );

  const append = useCallback((line) => {
    setLines((prev) => [...prev, line].slice(-MAX_TURNS));
  }, []);

  const submit = useCallback(
    async (event) => {
      event.preventDefault();
      const message = draft.trim();
      if (!message || isThinking) return;

      const turn = requestId.current + 1;
      requestId.current = turn;

      setDraft('');
      setError(null);
      append({ id: `u-${turn}`, role: 'user', text: message });
      setIsThinking(true);

      try {
        const { response, error: apiError } = await askMoony({ message, history });
        if (requestId.current !== turn) return; // a newer turn superseded this one
        append({ id: `m-${turn}`, role: 'moony', text: response });
        if (apiError) setError('moony is having trouble reaching the signal');
      } catch {
        if (requestId.current !== turn) return;
        append({
          id: `m-${turn}`,
          role: 'moony',
          text: '...the line went dead. say it again, quieter.',
        });
        setError('the backend did not answer');
      } finally {
        if (requestId.current === turn) setIsThinking(false);
      }
    },
    [append, draft, history, isThinking],
  );

  // AnimatePresence needs the node mounted to run the exit animation, so the
  // portal stays mounted while `open` flips.
  return createPortal(
    <AnimatePresence>
      {open && (
        <div className="dmr-modal-root" role="presentation">
          <motion.div
            className="dmr-modal__scrim"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.24 }}
            onClick={onClose}
            aria-hidden="true"
          />

          <motion.div
            className="dmr-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Moony, the night shift operator"
            variants={MODAL_VARIANTS}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            {/* Panel header */}
            <div className="dmr-panel__bar">
              <span className="dmr-dot dmr-dot--close" />
              <span className="dmr-dot dmr-dot--min" />
              <span className="dmr-dot dmr-dot--max" />
              <span className="dmr-panel__title">moony · night shift</span>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close chat with Moony"
                className="ml-auto text-[color:var(--muted)] transition-colors hover:text-[color:var(--phosphor)]"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Transcript */}
            <div
              ref={bodyRef}
              className="dmr-modal__body dmr-scroll flex flex-col gap-2"
              aria-live="polite"
            >
              <div className="mb-1 flex items-center gap-3">
                <motion.span
                  className="dmr-moon"
                  animate={{ opacity: [0.55, 1, 0.55] }}
                  transition={{ duration: 3.4, repeat: Infinity, ease: 'easeInOut' }}
                  aria-hidden="true"
                >
                  ☾
                </motion.span>
                <p className="dmr-hint">
                  sad, quiet, and only really awake when you mention music.
                </p>
              </div>

              {lines.map((line) => (
                <Bubble key={line.id} line={line} />
              ))}

              {isThinking && (
                <motion.div
                  className="dmr-bubble dmr-bubble--moony"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  aria-label="Moony is thinking"
                >
                  <span className="dmr-typing">
                    <span />
                    <span />
                    <span />
                  </span>
                </motion.div>
              )}

              {error && !isThinking && (
                <p className="dmr-bubble dmr-bubble--system dmr-line--error">{error}</p>
              )}
            </div>

            {/* Composer */}
            <form className="dmr-modal__composer" onSubmit={submit}>
              <span className="dmr-uplink__prompt" aria-hidden="true">
                &gt;
              </span>
              <input
                ref={inputRef}
                className="dmr-input"
                type="text"
                value={draft}
                maxLength={1000}
                placeholder="say something to moony..."
                onChange={(event) => setDraft(event.target.value)}
                data-testid="moony-input"
                aria-label="Message to Moony"
              />
              <button
                type="submit"
                className="dmr-button dmr-button--solid"
                disabled={isThinking || !draft.trim()}
                aria-label="Send message to Moony"
                data-testid="moony-send"
              >
                <Send className="h-3.5 w-3.5" />
              </button>
            </form>
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
